/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useTranslation } from "react-i18next";
import { type FC, useCallback, useEffect, type JSX, useRef } from "react";
import { Button } from "@vector-im/compound-web";
import { logger } from "matrix-js-sdk/lib/logger";

import styles from "./RageshakeButton.module.css";
import { useSubmitRageshake } from "./submit-rageshake";

interface Props {
  description: string;
  autoSubmit?: boolean;
}

export const RageshakeButton: FC<Props> = ({
  description,
  autoSubmit = false,
}) => {
  const { submitRageshake, sending, sent, error, available } =
    useSubmitRageshake();
  const { t } = useTranslation();
  const autoSubmitted = useRef(false);

  const sendDebugLogs = useCallback(() => {
    submitRageshake({
      description,
      sendLogs: true,
    }).catch((e) => {
      logger.error("Failed to send rageshake", e);
    });
  }, [submitRageshake, description]);

  useEffect(() => {
    if (!autoSubmit || !available || autoSubmitted.current || sending || sent)
      return;

    autoSubmitted.current = true;
    sendDebugLogs();
  }, [autoSubmit, available, sendDebugLogs, sending, sent]);

  if (!available) return null;

  let logsComponent: JSX.Element | null = null;
  if (sending) {
    logsComponent = <span>{t("rageshake_sending")}</span>;
  } else if (sent) {
    logsComponent = <div>{t("rageshake_sent")}</div>;
  } else {
    let caption = t("rageshake_send_logs");
    if (error) {
      caption = t("rageshake_button_error_caption");
    }

    logsComponent = (
      <Button
        onClick={sendDebugLogs}
        className={styles.wideButton}
        disabled={sending}
      >
        {caption}
      </Button>
    );
  }

  return <div className={styles.rageshakeControl}>{logsComponent}</div>;
};
